import { useState, useRef, useEffect } from 'react';
import { Heart, MessageCircle, Share2, Award, MoreHorizontal, ArrowUp, Code, Figma, Github, ChevronLeft, ChevronRight, ExternalLink, Send, MessageSquare, ChevronDown, Smile, Users, Trash2, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Badge } from "@/components/ui/badge";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/hooks/use-toast";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import EmojiPicker from './EmojiPicker';
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { useAuth } from '@/contexts/AuthContext';
import { deletePost, likePost, unlikePost, upvotePost, downvotePost, getPostComments, addComment, getPostInteractionStatus, sendMessage } from '@/lib/supabase';
import { availableTools } from '@/lib/tools';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import Comment from '@/components/Comment';

const toolIcons: Record<string, React.FC<{ className?: string }>> = {
  code: Code,
  figma: Figma,
  github: Github,
  react: () => <span className="text-sm">⚛️</span>,
  nextjs: () => <span className="text-sm">▲</span>,
  lovable: () => <span className="text-sm">🎵</span>,
};

interface CommentProps {
  id: string;
  user: {
    id: string | number;
    name: string;
    username: string;
    avatar_url: string;
  };
  content: string;
  createdAt: string;
  replies?: CommentProps[];
  upvotes: number;
}

export interface PostProps {
  id: string;
  user: {
    id: string;
    name: string;
    username: string;
    avatar_url?: string;
    level?: number;
    experience?: number;
  };
  content: string;
  created_at: string;
  likes: number;
  upvotes: number;
  comments: number;
  shares: number;
  images?: { image_url: string }[];
  link_url?: string;
  tools?: string[];
  openForCollaboration?: boolean;
  tags?: string[];
}

const Post = ({ post, onPostDeleted }: { post: PostProps, onPostDeleted?: (postId: string | number) => void }) => {
  if (!post) {
    console.error('Post component received undefined post object');
    return null;
  }

  const { user, profile } = useAuth();
  
  const [liked, setLiked] = useState(false);
  const [upvoted, setUpvoted] = useState(false);
  const [likeCount, setLikeCount] = useState(post.likes);
  const [upvoteCount, setUpvoteCount] = useState(post.upvotes);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showComments, setShowComments] = useState(false);
  const [commentContent, setCommentContent] = useState("");
  const [commentsData, setCommentsData] = useState<CommentProps[]>([]);
  const [commentCount, setCommentCount] = useState(post.comments);
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState("");
  const [showEmojiPickerForComment, setShowEmojiPickerForComment] = useState(false);
  const [showEmojiPickerForReply, setShowEmojiPickerForReply] = useState<string | null>(null);
  const [directMessage, setDirectMessage] = useState('');
  const [showDirectMessageDialog, setShowDirectMessageDialog] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [shareCount, setShareCount] = useState(post.shares);
  const [showSharePopover, setShowSharePopover] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isLikeLoading, setIsLikeLoading] = useState(false);
  const [isUpvoteLoading, setIsUpvoteLoading] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [modalImageIndex, setModalImageIndex] = useState(0);
  const navigationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const eventProcessingRef = useRef(false);

  // Debug post image properties
  console.log(`Post ${post.id} image data:`, { 
    imageUrl: post.images?.[0]?.image_url, 
    imageUrls: post.images?.map(img => img.image_url),
    imageCount: post.images?.length || 0
  });

  // Validate image URLs - filter out null, undefined, or empty strings
  const images = (post.images || [])
    .filter(img => !!img.image_url && typeof img.image_url === 'string' && img.image_url.trim() !== '')
    .map(img => {
      // Process the URL based on its format
      console.log(`[Post ${post.id}] Processing image URL: "${img.image_url}"`);
      
      // Handle null/undefined (shouldn't happen due to filter, but just in case)
      if (!img.image_url) return null;
      
      try {
        const url = img.image_url.trim();
        
        // If URL already has the full Supabase path, make sure it's properly formatted
        if (url.includes('supabase.co') && url.includes('storage/v1/object')) {
          // Ensure URL is properly formatted and using HTTPS
          const httpsUrl = url.replace(/^http:/, 'https:');
          return httpsUrl;
        }
        
        // Handle bucket/path format (our primary storage format)
        if (url.includes('/') && !url.startsWith('http')) {
          // Format is typically: "bucket_name/file_path.jpg"
          const bucketPath = url.trim();
          console.log(`[Post ${post.id}] Found bucket/path format: ${bucketPath}`);
          
          return `https://ibuwhrbyjyqrntgwjzsu.supabase.co/storage/v1/object/public/${bucketPath}`;
        }
        
        // Already a fully qualified URL, just ensure it's HTTPS
        if (url.startsWith('http://') || url.startsWith('https://')) {
          return url.replace(/^http:/, 'https:');
        }
        
        // Relative URL path, add origin
        if (url.startsWith('/')) {
          return `${window.location.origin}${url}`;
        }
        
        // Unknown format, try prefixing with Supabase storage URL
        console.log(`[Post ${post.id}] Using fallback for unknown URL format: ${url}`);
        return `https://ibuwhrbyjyqrntgwjzsu.supabase.co/storage/v1/object/public/${url}`;
      } catch (error) {
        console.error(`[Post ${post.id}] Error processing image URL: ${img.image_url}`, error);
        return null;
      }
    })
    .filter(Boolean); // Remove any null items
  
  const hasMultipleImages = images.length > 1;
  const hasCarouselImages = images.length > 1;
  const hasImages = images.length > 0;

  // Enhanced logging for image debugging
  console.log(`[Post ${post.id}] Processed images:`, { 
    images, 
    hasMultipleImages,
    hasCarouselImages,
    hasImages
  });

  // Pre-load images when component mounts
  useEffect(() => {
    if (hasImages) {
      // Reset error state and current index when images change
      setImageError(false);
      setCurrentImageIndex(0);
      
      // Preload images
      console.log(`Preloading ${images.length} images for post ${post.id}...`);
      images.forEach((src, index) => {
        const img = new Image();
        img.onload = () => console.log(`Preloaded image ${index} for post ${post.id}`);
        img.onerror = () => {
          console.warn(`Failed to preload image ${index} for post ${post.id}: ${src}`);
          // Try alternative URL format for Supabase
          if (src.includes('supabase.co')) {
            const alternativeUrl = src.replace(
              /storage\/v1\/object\/public\/([^\/]+)\/(.+)/,
              'storage/v1/object/sign/$1/$2'
            );
            console.log(`Trying alternative URL format: ${alternativeUrl}`);
            const altImg = new Image();
            altImg.src = alternativeUrl;
          }
        };
        img.src = src;
      });
    }
  }, [images, post.id]);

  // Handle keyboard arrow keys for image navigation
  useEffect(() => {
    if (!hasCarouselImages) return;
    
    const handleKeyDown = (e: KeyboardEvent) => {
      // Use a data attribute to ensure we only process keyboard events
      // when the user is actively viewing this specific post
      const postElement = document.getElementById(`post-${post.id}`);
      if (!postElement || !postElement.contains(document.activeElement)) {
        return;
      }
      
      // Prevent handling nav keys if already navigating
      if (isNavigating) return;
      
      if (e.key === 'ArrowRight') {
        console.log(`[KeyNav] Right arrow pressed for post ${post.id}`);
        e.preventDefault();
        setIsNavigating(true);
        nextImage();
        // Reset navigation state after a short delay
        if (navigationTimeoutRef.current) clearTimeout(navigationTimeoutRef.current);
        navigationTimeoutRef.current = setTimeout(() => setIsNavigating(false), 300);
      } else if (e.key === 'ArrowLeft') {
        console.log(`[KeyNav] Left arrow pressed for post ${post.id}`);
        e.preventDefault();
        setIsNavigating(true);
        prevImage();
        // Reset navigation state after a short delay
        if (navigationTimeoutRef.current) clearTimeout(navigationTimeoutRef.current);
        navigationTimeoutRef.current = setTimeout(() => setIsNavigating(false), 300);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (navigationTimeoutRef.current) clearTimeout(navigationTimeoutRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasCarouselImages, currentImageIndex, images.length, post.id, isNavigating]);

  // Check if the user has liked or upvoted the post
  useEffect(() => {
    const checkInteractions = async () => {
      if (!user) return;
      
      try {
        // Get all interaction data at once
        const status = await getPostInteractionStatus(post.id.toString(), user.id);
        
        // Update state with current values from the database
        setLiked(status.hasLiked);
        setUpvoted(status.hasUpvoted);
        
        // Use counter fields directly from the database
        setLikeCount(status.likesCount);
        setUpvoteCount(status.upvotesCount);
        setCommentCount(status.commentsCount);
        
        console.log(`[Post ${post.id}] Interaction status:`, status);
        
        // Load comments in the background so they're ready when needed
        loadComments();
      } catch (error) {
        console.error('Error checking post interactions:', error);
      }
    };
    
    checkInteractions();
  }, [post.id, user]);

  const formatTimeAgo = (dateString: string) => {
    const now = new Date();
    const past = new Date(dateString);
    const diffInSeconds = Math.floor((now.getTime() - past.getTime()) / 1000);
    
    if (diffInSeconds < 60) return `${diffInSeconds}s ago`;
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  };

  const handleLike = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!user) {
      toast({
        title: "Please sign in",
        description: "You need to be signed in to like posts",
        variant: "destructive",
      });
      return;
    }
    
    // Prevent multiple clicks while processing
    if (isLikeLoading) return;
    
    setIsLikeLoading(true);
    
    // Optimistically update UI state
    const wasLiked = liked;
    const previousCount = likeCount;
    
    setLiked(!wasLiked);
    setLikeCount(wasLiked ? previousCount - 1 : previousCount + 1);
    
    try {
      if (wasLiked) {
        // Remove like
        const success = await unlikePost(post.id.toString(), user.id);
        if (!success) {
          // Revert UI state if API call fails
          setLiked(wasLiked);
          setLikeCount(previousCount);
          toast({
            title: "Error",
            description: "Failed to remove like. Please try again.",
            variant: "destructive",
          });
        }
      } else {
        // Add like
        const success = await likePost(post.id.toString(), user.id);
        if (!success) {
          // Revert UI state if API call fails
          setLiked(wasLiked);
          setLikeCount(previousCount);
          toast({
            title: "Error",
            description: "Failed to like post. Please try again.",
            variant: "destructive",
          });
        }
      }
    } catch (error) {
      console.error('Error toggling like status:', error);
      // Revert UI state if there was an exception
      setLiked(wasLiked);
      setLikeCount(previousCount);
      toast({
        title: "Error",
        description: "Failed to update like status. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLikeLoading(false);
    }
  };

  const handleUpvote = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!user) {
      toast({
        title: "Please sign in",
        description: "You need to be signed in to upvote posts",
        variant: "destructive",
      });
      return;
    }
    
    // Prevent multiple clicks while processing
    if (isUpvoteLoading) return;
    
    setIsUpvoteLoading(true);
    
    // Optimistically update UI state
    const wasUpvoted = upvoted;
    const previousCount = upvoteCount;
    
    setUpvoted(!wasUpvoted);
    setUpvoteCount(wasUpvoted ? previousCount - 1 : previousCount + 1);
    
    try {
      if (wasUpvoted) {
        // Remove upvote
        const success = await downvotePost(post.id.toString(), user.id);
        if (!success) {
          // Revert UI state if API call fails
          setUpvoted(wasUpvoted);
          setUpvoteCount(previousCount);
          toast({
            title: "Error",
            description: "Failed to remove upvote. Please try again.",
            variant: "destructive",
          });
        } else if (post.user.id !== user.id) {
          toast({
            title: "XP Reduced",
            description: `Removed upvote from ${post.user.name}'s post`,
            variant: "default",
          });
        }
      } else {
        // Add upvote
        const success = await upvotePost(post.id.toString(), user.id);
        if (!success) {
          // Revert UI state if API call fails
          setUpvoted(wasUpvoted);
          setUpvoteCount(previousCount);
          toast({
            title: "Error",
            description: "Failed to upvote post. Please try again.",
            variant: "destructive",
          });
        } else if (post.user.id !== user.id) {
          toast({
            title: "XP Awarded",
            description: `${post.user.name} earned +3 XP from your upvote!`,
            variant: "default",
          });
        }
      }
    } catch (error) {
      console.error('Error toggling upvote status:', error);
      // Revert UI state if there was an exception
      setUpvoted(wasUpvoted);
      setUpvoteCount(previousCount);
      toast({
        title: "Error",
        description: "Failed to update upvote status. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUpvoteLoading(false);
    }
  };

  // Function to navigate to the next image
  const nextImage = () => {
    console.log(`[DEBUG] Next image handler called - Post ${post.id}, Current index: ${currentImageIndex}, Images count: ${images.length}`);
    
    if (currentImageIndex < images.length - 1) {
      setImageError(false);
      setCurrentImageIndex(currentImageIndex + 1);
    }
  };

  // Function to navigate to the previous image
  const prevImage = () => {
    console.log(`[DEBUG] Previous image handler called - Post ${post.id}, Current index: ${currentImageIndex}, Images count: ${images.length}`);
    
    if (currentImageIndex > 0) {
      setImageError(false);
      setCurrentImageIndex(currentImageIndex - 1);
    }
  };

  // Handler for image load errors
  const handleImageError = () => {
    console.error("[Image Error]", {
      src: images[currentImageIndex],
      index: currentImageIndex,
      imageType: typeof images[currentImageIndex],
      postId: post.id
    });
    
    try {
      // Try to load with alternative URL format before giving up
      const img = document.querySelector(`img[src="${images[currentImageIndex]}"]`) as HTMLImageElement;
      if (img && !img.dataset.retried) {
        img.dataset.retried = 'true';
        
        // Try alternative format
        const currentUrl = images[currentImageIndex];
        
        if (currentUrl) {
          // First attempt: Try direct download URL with a download parameter
          if (currentUrl.includes('supabase.co')) {
            // Try direct download URL which often works better than the signed URL
            const alternativeUrl = currentUrl.replace(
              /storage\/v1\/object\/public\/([^\/]+)\/(.+)/,
              'storage/v1/object/public/$1/$2?download=true'
            );
            
            console.log(`Trying alternative URL format: ${alternativeUrl}`);
            img.src = alternativeUrl;
            
            // Add a second retry attempt with signed URL if the direct download fails
            img.onerror = () => {
              if (!img.dataset.secondRetry) {
                img.dataset.secondRetry = 'true';
                const signedUrl = currentUrl.replace(
                  /storage\/v1\/object\/public\/([^\/]+)\/(.+)/,
                  'storage/v1/object/sign/$1/$2'
                );
                console.log(`Trying signed URL format: ${signedUrl}`);
                img.src = signedUrl;
                
                // Final fallback if that also fails
                img.onerror = () => {
                  console.error(`All image recovery attempts failed for: ${currentUrl}`);
                  setImageError(true);
                };
              } else {
                // If both retries failed, show error state
                setImageError(true);
              }
            };
            
            return;
          }
          
          // For non-Supabase URLs, try a cache-busting approach
          const cacheBustUrl = `${currentUrl}${currentUrl.includes('?') ? '&' : '?'}_cb=${Date.now()}`;
          console.log(`Trying cache-busting URL: ${cacheBustUrl}`);
          img.src = cacheBustUrl;
          
          img.onerror = () => {
            setImageError(true);
          };
          
          return;
        }
      }
    } catch (error) {
      console.error("Error in image error recovery:", error);
    }
    
    // If we get here, all recovery attempts failed
    setImageError(true);
  };

  const handleCommentClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowComments(!showComments);
    
    // Load comments when users clicks to view them
    if (!showComments) {
      loadComments();
    }
  };

  const loadComments = async () => {
    try {
      const comments = await getPostComments(post.id.toString());
      if (comments && comments.length > 0) {
        // Process comments to organize parent comments and replies
        const parentComments: CommentProps[] = [];
        const replyMap: Record<string, CommentProps[]> = {};
        
        // First pass: Identify parent comments and replies
        comments.forEach(comment => {
          const formattedComment = {
            id: comment.id,
            user: {
              id: comment.profiles?.id || 0,
              name: comment.profiles?.display_name || "Unknown User",
              username: comment.profiles?.username ? `@${comment.profiles.username}` : "@unknown",
              avatar: comment.profiles?.avatar_url || "/placeholder.svg",
            },
            content: comment.content,
            createdAt: comment.created_at,
            upvotes: comment.upvotes_count || 0,
            replies: [],
          };
          
          if (comment.parent_id) {
            // This is a reply, add to the reply map
            if (!replyMap[comment.parent_id]) {
              replyMap[comment.parent_id] = [];
            }
            replyMap[comment.parent_id].push(formattedComment);
          } else {
            // This is a parent comment
            parentComments.push(formattedComment);
          }
        });
        
        // Second pass: Attach replies to their parent comments
        parentComments.forEach(parentComment => {
          if (replyMap[parentComment.id]) {
            parentComment.replies = replyMap[parentComment.id];
          }
        });
        
        // Sort parent comments by creation date (newest first)
        parentComments.sort((a, b) => 
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        
        setCommentsData(parentComments);
        console.log(`Loaded ${parentComments.length} comments with ${
          Object.values(replyMap).flat().length
        } total replies for post ${post.id}`);
      } else {
        setCommentsData([]);
      }
    } catch (error) {
      console.error('Error loading comments:', error);
      toast({
        title: "Error",
        description: "Failed to load comments. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!commentContent.trim() || !user) {
      return;
    }
    
    const tempCommentId = `temp-${Date.now()}`;
    const optimisticComment: CommentProps = {
      id: tempCommentId,
      user: {
        id: user.id,
        name: profile?.display_name || user.email?.split('@')[0] || 'User',
        username: profile?.username ? `@${profile.username}` : `@${user.email?.split('@')[0] || 'user'}`,
        avatar_url: profile?.avatar_url || "/placeholder.svg",
      },
      content: commentContent,
      createdAt: new Date().toISOString(),
      upvotes: 0,
      replies: [],
    };
    
    // Optimistically update UI
    setCommentsData(prev => [optimisticComment, ...prev]);
    setCommentCount(prev => prev + 1);
    setCommentContent("");
    
    try {
      // Add comment to database
      const result = await addComment({
        post_id: post.id.toString(),
        user_id: user.id,
        content: commentContent,
      });
      
      if (result && result.id) {
        // Replace the temporary comment with the real one
        const realComment = {
          ...optimisticComment,
          id: result.id,
        };
        
        setCommentsData(prev => 
          prev.map(comment => 
            comment.id === tempCommentId ? realComment : comment
          )
        );
        
        toast({
          title: "Comment added",
          description: "Your comment has been added to the post",
        });
      } else {
        // If comment creation failed, remove the optimistic comment
        setCommentsData(prev => 
          prev.filter(comment => comment.id !== tempCommentId)
        );
        setCommentCount(prev => prev - 1);
        
        throw new Error("Failed to add comment");
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      
      // Remove optimistic comment if there was an error
      setCommentsData(prev => 
        prev.filter(comment => comment.id !== tempCommentId)
      );
      setCommentCount(prev => prev - 1);
      
      toast({
        title: "Error",
        description: "Failed to add comment. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleReply = (commentId: string) => {
    setReplyingTo(replyingTo === commentId ? null : commentId);
    setReplyContent("");
  };

  const handleAddReply = async (e: React.FormEvent, parentCommentId: string) => {
    e.preventDefault();
    
    if (!replyContent.trim() || !user || !parentCommentId) {
      return;
    }
    
    const tempReplyId = `temp-reply-${Date.now()}`;
    const optimisticReply: CommentProps = {
      id: tempReplyId,
      user: {
        id: user.id,
        name: profile?.display_name || user.email?.split('@')[0] || 'User',
        username: profile?.username ? `@${profile.username}` : `@${user.email?.split('@')[0] || 'user'}`,
        avatar_url: profile?.avatar_url || "/placeholder.svg",
      },
      content: replyContent,
      createdAt: new Date().toISOString(),
      upvotes: 0,
      replies: [],
    };
    
    // Optimistically update UI by adding the reply to the parent comment
    setCommentsData(prev => 
      prev.map(comment => {
        if (comment.id === parentCommentId) {
          return {
            ...comment,
            replies: [optimisticReply, ...(comment.replies || [])]
          };
        }
        return comment;
      })
    );
    
    // Clear input field and reset reply state
    setReplyContent("");
    setReplyingTo(null);
    
    try {
      // Add reply to database
      const result = await addComment({
        post_id: post.id.toString(),
        user_id: user.id,
        content: replyContent,
        parent_id: parentCommentId
      });
      
      if (result && result.id) {
        // Replace the temporary reply with the real one in the UI
        const realReply = {
          ...optimisticReply,
          id: result.id,
        };
        
        setCommentsData(prev => 
          prev.map(comment => {
            if (comment.id === parentCommentId && comment.replies) {
              return {
                ...comment,
                replies: comment.replies.map(reply => 
                  reply.id === tempReplyId ? realReply : reply
                )
              };
            }
            return comment;
          })
        );
        
        toast({
          title: "Reply added",
          description: "Your reply has been added to the comment",
        });
      } else {
        throw new Error("Failed to add reply");
      }
    } catch (error) {
      console.error('Error adding reply:', error);
      
      // Remove optimistic reply if there was an error
      setCommentsData(prev => 
        prev.map(comment => {
          if (comment.id === parentCommentId && comment.replies) {
            return {
              ...comment,
              replies: comment.replies.filter(reply => reply.id !== tempReplyId)
            };
          }
          return comment;
        })
      );
      
      toast({
        title: "Error",
        description: "Failed to add reply. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleEmojiSelectForComment = (emoji: string) => {
    setCommentContent((prev) => prev + emoji);
    setShowEmojiPickerForComment(false);
  };

  const handleEmojiSelectForReply = (emoji: string, commentId: string) => {
    setReplyContent((prev) => prev + emoji);
    setShowEmojiPickerForReply(null);
  };

  const sendDirectMessage = () => {
    if (!directMessage.trim() || !user) return;
    
    console.log('Sending direct message with:', {
      sender_id: user.id,
      receiver_id: post.user.id,
      subject: `Collaboration on post: ${post.content.substring(0, 30)}...`,
      content: directMessage,
      related_post_id: post.id
    });
    
    // Call the sendMessage function from supabase
    sendMessage({
      sender_id: user.id,
      receiver_id: post.user.id.toString(), // Ensure string type
      subject: `Collaboration on post: ${post.content.substring(0, 30)}...`,
      content: directMessage,
      related_post_id: post.id.toString() // Ensure string type
    }).then(result => {
      if (result) {
        toast({
          title: "Message sent!",
          description: `Your message has been sent to ${post.user.name}`,
        });
        setDirectMessage('');
        setShowDirectMessageDialog(false);
      } else {
        toast({
          title: "Error",
          description: "Failed to send message. Please try again.",
          variant: "destructive",
        });
      }
    }).catch(error => {
      console.error("Error sending message:", error);
      console.error("Error details:", JSON.stringify(error, null, 2));
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive",
      });
    });
  };

  const handleShare = async (platform: string, e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    
    const shareData = {
      title: `${post.user.name}'s post on Vibebops`,
      text: post.content.substring(0, 100) + (post.content.length > 100 ? '...' : ''),
      url: `${window.location.origin}/post/${post.id}`,
    };

    try {
      switch (platform) {
        case 'clipboard':
          await navigator.clipboard.writeText(shareData.url);
          toast({
            title: "Link copied",
            description: "Post link copied to clipboard",
          });
          break;
        case 'twitter':
          window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareData.text)}&url=${encodeURIComponent(shareData.url)}`, '_blank');
          break;
        case 'facebook':
          window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareData.url)}`, '_blank');
          break;
        case 'linkedin':
          window.open(`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareData.url)}&title=${encodeURIComponent(shareData.title)}&summary=${encodeURIComponent(shareData.text)}`, '_blank');
          break;
        case 'native':
          if (navigator.share) {
            await navigator.share(shareData);
          } else {
            await navigator.clipboard.writeText(shareData.url);
            toast({
              title: "Link copied",
              description: "Post link copied to clipboard (sharing not supported on this browser)",
            });
          }
          break;
      }
      
      setShareCount(prev => prev + 1);
      setShowSharePopover(false);
      
      toast({
        title: "Post shared",
        description: `You've shared this post on ${platform === 'clipboard' ? 'your clipboard' : platform}`,
      });
    } catch (error) {
      console.error('Error sharing:', error);
      toast({
        title: "Share failed",
        description: "There was a problem sharing this post",
        variant: "destructive",
      });
    }
  };

  const handleDeletePost = async () => {
    setIsDeleting(true);
    
    try {
      const result = await deletePost(post.id);
      
      if (result && result.success) {
        toast({
          title: "Post deleted",
          description: "Your post has been successfully deleted",
        });
        
        // Notify parent component that post was deleted
        if (onPostDeleted) {
          onPostDeleted(post.id);
        }
      } else {
        throw new Error(result?.error || "Unknown error deleting post");
      }
    } catch (error) {
      console.error("Error deleting post:", error);
      toast({
        title: "Error",
        description: "Failed to delete post. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };
  
  // Check if current user is the post author
  const isPostAuthor = user?.id && user.id.toString() === post.user.id.toString();

  // Render user level badge if available
  const renderUserLevel = () => {
    if (!post.user) return null;
    return (
      <div className="flex items-center gap-1">
        <Badge variant="outline" className="text-xs">
          Level {post.user.level || 1}
        </Badge>
        {post.user.experience && (
          <div className="text-xs text-vibebops-textMuted">
            {post.user.experience} XP
          </div>
        )}
      </div>
    );
  };

  // Add debug logging for tools
  useEffect(() => {
    if (post.tools) {
      console.log("Post tools data:", {
        postId: post.id,
        hasTools: !!post.tools,
        toolsLength: post.tools?.length || 0,
        toolsData: post.tools
      });
    }
  }, [post.id, post.tools]);

  const openImageModal = (index: number) => {
    setModalImageIndex(index);
    setShowImageModal(true);
  };

  const closeImageModal = () => {
    setShowImageModal(false);
  };

  const nextModalImage = () => {
    if (modalImageIndex < images.length - 1) {
      setModalImageIndex(prev => prev + 1);
    }
  };

  const prevModalImage = () => {
    if (modalImageIndex > 0) {
      setModalImageIndex(prev => prev - 1);
    }
  };

  // Handle keyboard navigation in modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!showImageModal) return;
      
      if (e.key === 'ArrowRight') {
        nextModalImage();
      } else if (e.key === 'ArrowLeft') {
        prevModalImage();
      } else if (e.key === 'Escape') {
        closeImageModal();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showImageModal, modalImageIndex, images.length]);

  const handlePrevImageClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    console.log(`[Button Handler] Previous button clicked for post ${post.id}`);
    if (currentImageIndex > 0) {
      prevImage();
    }
  };

  const handleNextImageClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    console.log(`[Button Handler] Next button clicked for post ${post.id}`);
    if (currentImageIndex < images.length - 1) {
      nextImage();
    }
  };

  return (
    <div 
      className="vibebops-card mb-4 max-w-[800px] mx-auto"
      id={`post-${post.id}`} 
    >
      <div className="flex justify-between items-start mb-3">
        <div className="flex gap-3">
          <Link to={`/profile/${post.user.id}`}>
            <Avatar className="h-8 w-8 border-2 border-vibebops-highlight">
              <AvatarImage src={post.user.avatar_url} alt={post.user.name} />
              <AvatarFallback className="bg-gradient-to-r from-pink-500 to-violet-500 text-white font-bold">
                {post.user.name.charAt(0)}
              </AvatarFallback>
            </Avatar>
          </Link>
          <div>
            <div className="flex items-center gap-2">
              <Link to={`/profile/${post.user.id}`} className="font-medium text-sm hover:underline">
                {post.user.name}
              </Link>
              {renderUserLevel()}
              <span className="text-xs text-vibebops-textMuted">
                {formatTimeAgo(post.created_at)}
              </span>
            </div>
            <p className="text-xs text-vibebops-textMuted">{post.user.username}</p>
          </div>
        </div>
        
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="text-vibebops-textMuted hover:text-vibebops-text" type="button">
                <MoreHorizontal className="h-5 w-5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-vibebops-card border-vibebops-border">
              {isPostAuthor && (
                <>
                  <AlertDialogTrigger asChild>
                    <DropdownMenuItem className="text-red-500 cursor-pointer">
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Post
                    </DropdownMenuItem>
                  </AlertDialogTrigger>
                  
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuItem>Report Post</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          <AlertDialogContent className="bg-vibebops-card border-vibebops-border">
            <AlertDialogHeader>
              <AlertDialogTitle>Are you sure?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. This will permanently delete your post and remove it from our servers.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDeletePost} 
                className="bg-red-500 hover:bg-red-600" 
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
      
      <div className="mb-3">
        <p className="mb-2 whitespace-pre-line">{post.content}</p>
        
        {/* Image display section */}
        {hasImages && (
          <div className="relative rounded-2xl overflow-hidden mb-3 max-h-80 bg-vibebops-highlight/10">
            {/* When there are exactly 2 images, display them side by side */}
            {false && hasMultipleImages && !hasCarouselImages ? (
              <div className="grid grid-cols-2 gap-1 h-full">
                {images.map((imgSrc, idx) => (
                  <div 
                    key={`img-${idx}`}
                    className="relative cursor-pointer h-full"
                    onClick={() => openImageModal(idx)}
                  >
                    <img 
                      src={imgSrc} 
                      alt={`Post content ${idx + 1}`} 
                      className="w-full h-full object-cover bg-black/5 rounded-xl"
                      onError={handleImageError}
                      loading="lazy"
                      referrerPolicy="no-referrer"
                      crossOrigin="anonymous"
                      decoding="async"
                    />
                  </div>
                ))}
              </div>
            ) : (
              /* For single image or 3+ images (carousel mode) */
              <AspectRatio ratio={16/9} className="h-auto max-h-80 rounded-xl overflow-hidden relative">
                {/* Main container */}
                <div className="w-full h-full flex justify-center items-center">
                  {/* Image container */}
                  {imageError ? (
                    <div className="w-full h-full flex items-center justify-center bg-vibebops-highlight rounded-xl">
                      <p className="text-vibebops-textMuted">Could not load image</p>
                    </div>
                  ) : (
                    <img 
                      src={images[currentImageIndex]} 
                      alt={`Post content ${currentImageIndex + 1}`} 
                      className="w-full h-full object-contain bg-black/5 rounded-xl cursor-pointer"
                      onError={handleImageError}
                      loading="lazy"
                      referrerPolicy="no-referrer"
                      crossOrigin="anonymous"
                      decoding="async"
                      fetchPriority="high"
                      onClick={() => openImageModal(currentImageIndex)}
                    />
                  )}
                  
                  {/* Only show image counter in post card view - no navigation buttons */}
                  {hasCarouselImages && (
                    <div className="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full z-20">
                      {currentImageIndex + 1} / {images.length}
                    </div>
                  )}
                </div>
              </AspectRatio>
            )}
          </div>
        )}
        
        {post.link_url && (
          <div className="mb-3">
            <a 
              href={post.link_url} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="block p-3 rounded-lg bg-vibebops-highlight text-sm hover:bg-vibebops-highlight/70 mb-2"
            >
              <div className="flex items-center gap-2">
                <span className="h-4 w-4 flex-shrink-0">🔗</span>
                <span className="truncate">{post.link_url}</span>
              </div>
            </a>
            <a 
              href={post.link_url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex justify-start"
            >
              <Button size="sm" className="w-auto px-6 bg-vibebops-accent hover:bg-vibebops-accent/90 text-white font-bold text-xs py-1">
                <ExternalLink className="h-3 w-3 mr-1" />
                Visit Site
              </Button>
            </a>
          </div>
        )}
        
        <div className="flex flex-wrap gap-2 mb-3">
          {/* Tool badges display */}
          {Array.isArray(post.tools) && post.tools.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2">
              {post.tools.map((toolId, idx) => {
                // Look up tool in available tools
                const tool = availableTools.find(t => t.id === toolId);
                
                if (tool) {
                  const ToolIcon = tool.icon;
                  return (
                    <div key={`tool-${idx}`} className="tool-badge">
                      {ToolIcon && <ToolIcon className="h-3 w-3 mr-1" />}
                      <span className="capitalize">{tool.name}</span>
                    </div>
                  );
                }
                
                // Fallback for tools that don't match our list
                return (
                  <div key={`tool-${idx}`} className="tool-badge">
                    <span className="capitalize">{toolId}</span>
                  </div>
                );
              })}
            </div>
          )}
          
          {post.openForCollaboration && (
            <Dialog open={showDirectMessageDialog} onOpenChange={setShowDirectMessageDialog}>
              <DialogTrigger asChild>
                <Badge 
                  variant="secondary" 
                  className="flex items-center gap-1 py-1 px-3 cursor-pointer bg-purple-500 text-white border-purple-700 hover:bg-purple-600 transition-colors"
                >
                  <Users className="h-3 w-3" />
                  <span>Open For Collaboration</span>
                </Badge>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[425px] bg-vibebops-card">
                <DialogHeader>
                  <DialogTitle className="text-xl">Message {post.user.name}</DialogTitle>
                </DialogHeader>
                <div className="py-4">
                  <p className="text-vibebops-textMuted mb-4">
                    This post is open for collaboration. Send a direct message to the creator to discuss collaboration opportunities.
                  </p>
                  <Textarea 
                    placeholder="Write your pitch here..." 
                    value={directMessage}
                    onChange={(e) => setDirectMessage(e.target.value)}
                    className="min-h-[120px] mb-2"
                  />
                </div>
                <DialogFooter>
                  <DialogClose asChild>
                    <Button variant="outline" className="mr-2">Cancel</Button>
                  </DialogClose>
                  <Button onClick={sendDirectMessage} disabled={!directMessage.trim()}>
                    <Send className="h-4 w-4 mr-2" />
                    Send Message
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
        
        {post.tags && post.tags.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {post.tags.map((tag, idx) => (
              <Link key={idx} to={`/tag/${tag.replace('#', '')}`} className="tag">
                {tag}
              </Link>
            ))}
          </div>
        )}
      </div>
      
      <div className="flex justify-between items-center text-vibebops-textMuted border-t border-vibebops-border pt-2">
        <button 
          onClick={handleLike}
          disabled={isLikeLoading}
          className={`flex items-center gap-1 p-1 rounded-md hover:bg-vibebops-highlight ${liked ? 'text-red-500' : ''}`}
          type="button"
        >
          <Heart className={`h-4 w-4 ${liked ? 'fill-current' : ''}`} />
          <span className="text-sm">{likeCount}</span>
        </button>
        
        <button 
          onClick={handleUpvote}
          disabled={isUpvoteLoading}
          className={`flex items-center gap-1 p-1 rounded-md hover:bg-vibebops-highlight ${upvoted ? 'text-vibebops-accent2' : ''}`}
          type="button"
        >
          <ArrowUp className={`h-4 w-4 ${upvoted ? 'fill-current' : ''}`} />
          <span className="text-sm">{upvoteCount}</span>
        </button>
        
        <button 
          onClick={handleCommentClick}
          className={`flex items-center gap-1 p-1 rounded-md hover:bg-vibebops-highlight ${showComments ? 'text-vibebops-accent' : ''}`}
          type="button"
        >
          <MessageCircle className="h-4 w-4" />
          <span className="text-sm">{commentCount}</span>
        </button>
        
        <Popover open={showSharePopover} onOpenChange={setShowSharePopover}>
          <PopoverTrigger asChild>
            <button 
              onClick={(e) => e.preventDefault()}
              className="flex items-center gap-1 p-1 rounded-md hover:bg-vibebops-highlight"
            >
              <Share2 className="h-4 w-4" />
              <span className="text-sm">{shareCount}</span>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-56 bg-vibebops-card border-vibebops-border">
            <div className="grid gap-2">
              <h4 className="font-semibold mb-1">Share this post</h4>
              <Button 
                variant="outline" 
                className="justify-start"
                onClick={(e) => handleShare('clipboard', e)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path><rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect></svg>
                Copy link
              </Button>
              <Button 
                variant="outline" 
                className="justify-start"
                onClick={(e) => handleShare('twitter', e)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2"><path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z"></path></svg>
                Twitter
              </Button>
              <Button 
                variant="outline" 
                className="justify-start"
                onClick={(e) => handleShare('facebook', e)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path></svg>
                Facebook
              </Button>
              <Button 
                variant="outline" 
                className="justify-start"
                onClick={(e) => handleShare('linkedin', e)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path><rect x="2" y="9" width="4" height="12"></rect><circle cx="4" cy="4" r="2"></circle></svg>
                LinkedIn
              </Button>
              {navigator.share && (
                <Button 
                  variant="default" 
                  className="w-full mt-1"
                  onClick={(e) => handleShare('native', e)}
                >
                  Share via...
                </Button>
              )}
            </div>
          </PopoverContent>
        </Popover>
      </div>

      <Collapsible open={showComments} onOpenChange={setShowComments} className="mt-4 border-t border-vibebops-border pt-4">
        <CollapsibleTrigger asChild>
          <Button variant="ghost" className="flex items-center justify-between w-full">
            <span className="font-medium">Comments ({commentCount})</span>
            <ChevronDown className={`h-5 w-5 transition-transform ${showComments ? 'rotate-180' : ''}`} />
          </Button>
        </CollapsibleTrigger>
        
        <CollapsibleContent className="mt-2">
          {showComments && (
            <div className="border-t border-vibebops-border mt-3 pt-4">
              {/* Comment form */}
              <form 
                onSubmit={handleAddComment} 
                className="flex items-start gap-3 mb-4"
              >
                <div className="flex-shrink-0">
                  <Avatar className="w-8 h-8 border border-vibebops-highlight">
                    <AvatarImage src={profile?.avatar_url || "/placeholder.svg"} alt={profile?.display_name || "Your avatar"} />
                    <AvatarFallback className="bg-gradient-to-r from-pink-500 to-violet-500 text-white font-bold">
                      {profile?.display_name?.[0]?.toUpperCase() || "U"}
                    </AvatarFallback>
                  </Avatar>
                </div>
                
                <div className="flex-1">
                  <div className="flex gap-2 mb-2">
                    <Textarea 
                      placeholder="Add a comment..." 
                      value={commentContent}
                      onChange={(e) => setCommentContent(e.target.value)}
                      className="flex-1 min-h-[40px] resize-none"
                    />
                    <div className="flex flex-col gap-2">
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="icon"
                        onClick={() => setShowEmojiPickerForComment(!showEmojiPickerForComment)}
                        className="flex-shrink-0 text-vibebops-textMuted hover:text-vibebops-text"
                      >
                        <Smile className="h-5 w-5" />
                      </Button>
                      <Button 
                        type="submit" 
                        variant="ghost" 
                        size="icon"
                        className={`flex-shrink-0 ${!commentContent.trim() ? 'text-vibebops-textMuted' : 'text-vibebops-accent'}`}
                        disabled={!commentContent.trim()}
                      >
                        <Send className="h-5 w-5" />
                      </Button>
                    </div>
                  </div>
                  {showEmojiPickerForComment && (
                    <div className="mt-1">
                      <EmojiPicker onEmojiSelect={handleEmojiSelectForComment} />
                    </div>
                  )}
                </div>
              </form>
              
              <div className="space-y-4">
                {commentsData.length > 0 ? (
                  commentsData.map((comment) => (
                    <div key={comment.id} className="border-b border-vibebops-border pb-4 last:border-0">
                      <Comment 
                        comment={comment}
                        onReply={() => handleReply(comment.id)} 
                      />
                      
                      {replyingTo === comment.id && (
                        <form 
                          onSubmit={(e) => handleAddReply(e, comment.id)} 
                          className="mt-3 mb-2 ml-11"
                        >
                          <div className="flex gap-2">
                            <Avatar className="w-6 h-6 flex-shrink-0 border border-vibebops-highlight">
                              <AvatarImage src={profile?.avatar_url || "/placeholder.svg"} alt={profile?.display_name || "Your avatar"} />
                              <AvatarFallback className="bg-gradient-to-r from-pink-500 to-violet-500 text-white font-bold">
                                {profile?.display_name?.[0]?.toUpperCase() || "U"}
                              </AvatarFallback>
                            </Avatar>
                            
                            <div className="flex-1">
                              <div className="flex gap-2 mb-2">
                                <Textarea 
                                  placeholder="Write a reply..." 
                                  value={replyContent}
                                  onChange={(e) => setReplyContent(e.target.value)}
                                  className="flex-1 min-h-[40px] resize-none text-sm"
                                />
                                <div className="flex flex-col gap-2">
                                  <Button 
                                    type="button" 
                                    variant="ghost" 
                                    size="icon"
                                    onClick={() => setShowEmojiPickerForReply(showEmojiPickerForReply === comment.id ? null : comment.id)}
                                    className="flex-shrink-0 text-vibebops-textMuted hover:text-vibebops-text"
                                  >
                                    <Smile className="h-4 w-4" />
                                  </Button>
                                  <Button 
                                    type="submit" 
                                    variant="ghost" 
                                    size="icon"
                                    className={`flex-shrink-0 ${!replyContent.trim() ? 'text-vibebops-textMuted' : 'text-vibebops-accent'}`}
                                    disabled={!replyContent.trim()}
                                  >
                                    <Send className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                              {showEmojiPickerForReply === comment.id && (
                                <div className="mt-1">
                                  <EmojiPicker 
                                    onEmojiSelect={(emoji) => handleEmojiSelectForReply(emoji, comment.id)} 
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        </form>
                      )}
                      
                      {comment.replies && comment.replies.length > 0 && (
                        <div className="ml-11 mt-3 space-y-3">
                          {comment.replies.map((reply) => (
                            <Comment 
                              key={reply.id} 
                              comment={reply}
                              isReply={true}
                              level={1}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="text-center text-vibebops-textMuted text-sm py-2">
                    No comments yet. Be the first to comment!
                  </p>
                )}
              </div>
            </div>
          )}
        </CollapsibleContent>
      </Collapsible>

      {/* Image Modal */}
      <Dialog open={showImageModal} onOpenChange={setShowImageModal}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] p-0 bg-black border-none overflow-hidden" onClick={(e) => e.stopPropagation()}>
          <div className="relative w-full h-full flex items-center justify-center">
            {/* Close button */}
            <DialogClose className="absolute top-2 right-2 z-50 rounded-full bg-black/70 p-1 text-white hover:bg-black/90">
              <X className="h-6 w-6" />
            </DialogClose>
            
            {/* Image counter */}
            {images.length > 1 && (
              <div className="absolute top-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full z-10">
                {modalImageIndex + 1} / {images.length}
              </div>
            )}
            
            {/* Image */}
            <div className="w-full h-full flex items-center justify-center">
              <img 
                src={images[modalImageIndex]} 
                alt={`Post content ${modalImageIndex + 1}`} 
                className="max-w-full max-h-[90vh] object-contain"
                onError={() => setImageError(true)}
                loading="eager"
                referrerPolicy="no-referrer"
                crossOrigin="anonymous"
              />
            </div>
            
            {/* Image pagination indicators */}
            {images.length > 1 && (
              <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex gap-1 z-20">
                {images.map((_, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => {
                      console.log(`[Modal] Clicking image indicator ${idx}`);
                      setModalImageIndex(idx);
                    }}
                    className={`w-2 h-2 rounded-full ${
                      idx === modalImageIndex ? 'bg-white' : 'bg-white/40'
                    } hover:bg-white transition-colors`}
                    aria-label={`Go to image ${idx + 1}`}
                  />
                ))}
              </div>
            )}
            
            {/* Navigation arrows for multiple images */}
            {images.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={prevModalImage}
                  disabled={modalImageIndex === 0}
                  className={`absolute left-2 top-1/2 transform -translate-y-1/2 z-20 bg-black/70 text-white rounded-full p-3 cursor-pointer shadow-md ${
                    modalImageIndex === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-black/90 active:bg-black'
                  }`}
                  aria-label="Previous image"
                >
                  <ChevronLeft className="h-8 w-8" />
                </button>
                
                <button
                  type="button"
                  onClick={nextModalImage}
                  disabled={modalImageIndex === images.length - 1}
                  className={`absolute right-2 top-1/2 transform -translate-y-1/2 z-20 bg-black/70 text-white rounded-full p-3 cursor-pointer shadow-md ${
                    modalImageIndex === images.length - 1 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-black/90 active:bg-black'
                  }`}
                  aria-label="Next image"
                >
                  <ChevronRight className="h-8 w-8" />
                </button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Post;
